import React, { useState } from "react";

// Everything recorded against one zone or one hall, opened by clicking its
// cell in Zone View / Hall View. SvgCanvas collects the rows (the same rows
// the cell's colour is rolled up from); this only lays them out, in three
// sections: exhibitors (details + orders), service partners (details + their
// tasks with deliverables), and the hall's daily routines - which are per
// hall in the SP, with no exhibitor or stall on them.

const SMALL_STYLE = { fontSize: "11px" };
const SUBHEAD_STYLE = { fontSize: "10.5px", letterSpacing: "0.5px", textTransform: "uppercase" };

const matches = (query, ...fields) =>
  !query || fields.some((f) => f != null && String(f).toLowerCase().includes(query));

const exhibitorMatches = (query, ex) =>
  matches(query, ex.company, ex.regNo, ex.email, ex.mobile, ex.contactName, ex.stalls.join(" ")) ||
  ex.orders.some((o) => matches(query, o.serviceName, o.vendorName, o.contactPerson, o.remark)) ||
  ex.possession.some((p) => matches(query, p.status, p.issuedBy, p.takenBy));

const partnerMatches = (query, sp) =>
  matches(query, sp.company, sp.person, sp.mobile, sp.email, sp.services.join(" ")) ||
  sp.tasks.some((t) =>
    matches(query, t.serviceName, t.stall, t.exhibitor) ||
    t.deliverables.some((d) => matches(query, d.name)));

const formatDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString();
};

const SCOPE_PREVIEW = 4;

// Long lists are paged so a busy hall or zone does not turn the panel into one
// endless scroll. Cards (exhibitors, service partners, routines) are tall, so
// fewer per page than the small stall chips.
const CARD_PAGE_SIZE = 10;
const STALL_PAGE_SIZE = 60;

const pageOf = (items, page, size) => {
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(Math.max(1, page || 1), pages);
  return { items: items.slice((current - 1) * size, current * size), page: current, pages, size, total: items.length };
};

// Compact pager for the 430px panel: first / prev / "11-20 of 45" / next / last.
const Pager = ({ paged, onPage }) => {
  if (paged.pages <= 1) return null;
  const from = (paged.page - 1) * paged.size + 1;
  const to = Math.min(paged.page * paged.size, paged.total);
  const btn = (icon, target, label, disabled) => (
    <button
      type="button"
      className="btn btn-light border btn-sm px-1 py-0"
      style={{ fontSize: "12px", lineHeight: 1.4 }}
      disabled={disabled}
      aria-label={label}
      title={label}
      onClick={() => onPage(target)}
    >
      <i className={icon}></i>
    </button>
  );
  return (
    <div className="d-flex align-items-center justify-content-center gap-1 my-1" style={SMALL_STYLE}>
      {btn("ri-skip-back-mini-line", 1, "First page", paged.page === 1)}
      {btn("ri-arrow-left-s-line", paged.page - 1, "Previous page", paged.page === 1)}
      <span className="text-muted px-1">
        {from}-{to} of {paged.total} · page {paged.page}/{paged.pages}
      </span>
      {btn("ri-arrow-right-s-line", paged.page + 1, "Next page", paged.page === paged.pages)}
      {btn("ri-skip-forward-mini-line", paged.pages, "Last page", paged.page === paged.pages)}
    </div>
  );
};

// One deliverable planned on the stall, with its checklist sub points.
const DeliverableRow = ({ d, statusBadgeStyle }) => {
  const doneSubs = d.subPoints.filter((sp) => sp.done).length;
  const approvals = [
    d.hallManagerApproved && "Hall Manager",
    d.zoneManagerApproved && "Zone Manager",
    d.organizerApproved && "Organizer",
  ].filter(Boolean);
  return (
    <div className="border-top pt-1 mt-1">
      <div className="d-flex justify-content-between align-items-start gap-2">
        <span className="text-dark" style={{ fontSize: "11px" }}>{d.name}</span>
        <span className="badge flex-shrink-0" style={{ ...statusBadgeStyle(d.status), fontSize: "9.5px" }}>
          {d.status}
        </span>
      </div>
      <div className="text-muted" style={{ fontSize: "10.5px" }}>
        {(d.start || d.end) && (
          <span className="me-2"><i className="ri-time-line me-1"></i>{d.start || "--"} → {d.end || "--"}</span>
        )}
        {d.completed && d.completedAt && <span className="me-2">Done {d.completedAt}</span>}
        {d.overdue && <span className="fw-semibold me-2" style={{ color: "#ef4444" }}>{d.overdue}</span>}
        {approvals.length > 0 && (
          <span className="text-success"><i className="ri-shield-check-line me-1"></i>{approvals.join(", ")}</span>
        )}
      </div>
      {d.subPoints.length > 0 && (
        <div className="ms-2" style={{ fontSize: "10.5px" }}>
          <div className="text-muted">Sub points {doneSubs}/{d.subPoints.length}</div>
          {d.subPoints.map((sp) => (
            <div key={sp.id} className={sp.done ? "text-success" : "text-muted"}>
              <i className={`${sp.done ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} me-1`}></i>
              {sp.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// A service partner task: who does it, then what is to be delivered - the
// deliverables planned on this stall, or, where none are planned yet, the
// service's deliverable list from the work order.
const TaskCard = ({ t, infoIcon, badge, statusBadgeStyle }) => {
  const [showAllScope, setShowAllScope] = useState(false);
  const done = t.deliverables.filter((d) => d.completed).length;
  const scope = showAllScope ? t.scope : t.scope.slice(0, SCOPE_PREVIEW);
  return (
    <div className="border rounded p-2 mb-1 bg-white">
      <div className="d-flex justify-content-between align-items-start">
        <span className="fw-semibold text-dark" style={{ fontSize: "11.5px" }}>
          {t.serviceName || "Service"}
          {infoIcon}
        </span>
        {badge(t.status)}
      </div>
      <div className="text-muted" style={SMALL_STYLE}>
        <div>
          <strong>Stall:</strong> {t.stall || "--"} · <strong>Exhibitor:</strong> {t.exhibitor || "--"}
        </div>
        {t.overdue && (
          <div className="fw-semibold" style={{ color: "#ef4444" }}>
            <i className="ri-error-warning-fill me-1"></i>
            {t.overdue}
            {t.overdueCount > 1 ? ` (${t.overdueCount} checklist items past due)` : ""}
          </div>
        )}
      </div>

      {t.deliverables.length > 0 ? (
        <div className="mt-1">
          <div className="fw-semibold text-secondary" style={SUBHEAD_STYLE}>
            Deliverables ({done}/{t.deliverables.length} done)
          </div>
          {t.deliverables.map((d) => (
            <DeliverableRow key={d.id} d={d} statusBadgeStyle={statusBadgeStyle} />
          ))}
        </div>
      ) : t.scope.length > 0 ? (
        <div className="mt-1">
          <div className="fw-semibold text-secondary" style={SUBHEAD_STYLE}>
            Deliverables - not yet planned on this stall ({t.scope.length})
          </div>
          <ol className="text-muted mb-0 ps-3" style={{ fontSize: "10.5px" }}>
            {scope.map((s) => <li key={s.id}>{s.name}</li>)}
          </ol>
          {t.scope.length > SCOPE_PREVIEW && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              style={{ fontSize: "10.5px" }}
              onClick={() => setShowAllScope((v) => !v)}
            >
              {showAllScope ? "Show less" : `Show all ${t.scope.length}`}
            </button>
          )}
        </div>
      ) : (
        <div className="text-muted fst-italic mt-1" style={{ fontSize: "10.5px" }}>
          No deliverables in the service partner's plan.
        </div>
      )}
    </div>
  );
};

const AreaDetailPanel = ({
  area,
  // Other area (no booths): its type, e.g. "Food Court · Amber Hall". Only
  // service partner tasks and daily routines apply, so the exhibitor and
  // stall sections are left out.
  otherArea = null,
  detail,
  flags,
  isTaskLoading,
  onClose,
  onOpenManualForm,
  // A routine card clicked -> its Daily Routine Task update screen (edit mode).
  onOpenDailyRoutine,
  onSelectBooth,
  getStatusColor,
  statusBadgeStyle,
}) => {
  const [search, setSearch] = useState("");
  const [openIds, setOpenIds] = useState(() => new Set());
  const [stallsOpen, setStallsOpen] = useState(false);
  const [routinesOpen, setRoutinesOpen] = useState(true);
  // Current page per list; reset whenever the search changes.
  const [pageBy, setPageBy] = useState({});
  const setPage = (key) => (page) => setPageBy((prev) => ({ ...prev, [key]: page }));
  const q = search.trim().toLowerCase();

  const badge = (status, size = "10px") =>
    status ? (
      <span className="badge flex-shrink-0" style={{ ...statusBadgeStyle(status), fontSize: size }}>
        {status}
      </span>
    ) : null;

  const infoIcon = (serviceName, ex) =>
    ex.exhRegistrationID != null && serviceName && onOpenManualForm ? (
      <span
        role="button"
        className="ms-1 text-primary"
        style={{ cursor: "pointer", fontSize: "13px", lineHeight: 1 }}
        title={`Open the ${serviceName} form for ${ex.company || "this exhibitor"}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenManualForm(serviceName, ex.exhRegistrationID, ex.company);
        }}
      >
        <i className="ri-information-line"></i>
      </span>
    ) : null;

  const exhibitors = detail.exhibitors.filter((ex) => exhibitorMatches(q, ex));
  const partners = detail.servicePartners.filter((sp) => partnerMatches(q, sp));
  const stalls = detail.stalls.filter((s) => matches(q, s.name));
  const routines = detail.routines.filter((r) => matches(q, r.title, r.responsible));
  const exhibitorPage = pageOf(exhibitors, pageBy.exhibitors, CARD_PAGE_SIZE);
  const partnerPage = pageOf(partners, pageBy.partners, CARD_PAGE_SIZE);
  const routinePage = pageOf(routines, pageBy.routines, CARD_PAGE_SIZE);
  const stallPage = pageOf(stalls, pageBy.stalls, STALL_PAGE_SIZE);
  const isVenueZone = area.kind === "venueZone";
  const areaLabel = isVenueZone ? "venue zone" : area.kind === "zone" ? "zone" : "hall";
  const routineScope = isVenueZone ? area.zoneName : area.hallName;
  // A venue zone spans halls, so its stalls are listed hall by hall.
  const stallGroups = isVenueZone
    ? Array.from(
      stallPage.items.reduce((m, st) => m.set(st.hall || "--", (m.get(st.hall || "--") || []).concat(st)), new Map())
    ).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
    : [[null, stallPage.items]];

  // A search opens every card it finds, so the hit is visible. Ids are
  // prefixed ("ex:" / "sp:") - exhibitor and service partner ids can collide.
  const isOpen = (id) => (q ? true : openIds.has(id));
  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allOpenOf = (ids) => ids.length > 0 && ids.every((id) => openIds.has(id));
  const toggleAll = (ids) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (allOpenOf(ids)) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  // Expand / collapse all acts on the page being shown.
  const exhibitorIds = exhibitorPage.items.map((ex) => `ex:${ex.id}`);
  const partnerIds = partnerPage.items.map((sp) => `sp:${sp.id}`);
  const sectionHeader = (icon, title, count, ids) => (
    <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
      <span className="fw-semibold text-dark" style={{ fontSize: "12.5px" }}>
        <i className={`${icon} me-1`}></i>{title} ({count})
      </span>
      {ids.length > 0 && !q && (
        <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: "11px" }} onClick={() => toggleAll(ids)}>
          {allOpenOf(ids) ? "Collapse all" : "Expand all"}
        </button>
      )}
    </div>
  );

  const renderOrder = (ord, ex) => (
    <div key={ord.id} className="border rounded p-2 mb-1 bg-white">
      <div className="d-flex justify-content-between align-items-start">
        <span className="fw-semibold text-dark" style={{ fontSize: "11.5px" }}>
          {ord.serviceName}
          {infoIcon(ord.serviceName, ex)}
        </span>
        {badge(ord.status)}
      </div>
      <div className="text-muted" style={SMALL_STYLE}>
        <div><strong>Stall:</strong> {ord.stall || "--"}</div>
        <div><strong>Service Partner:</strong> {ord.vendorName || "--"}</div>
        {ord.contactPerson && (
          <div><strong>Contact:</strong> {ord.contactPerson}{ord.contactNo ? ` · ${ord.contactNo}` : ""}</div>
        )}
        {formatDate(ord.scheduledDate) && <div><strong>Scheduled:</strong> {formatDate(ord.scheduledDate)}</div>}
        {ord.lastUpdatedBy && <div><strong>Last updated by:</strong> {ord.lastUpdatedBy}</div>}
        {ord.remark && <div><strong>Remark:</strong> {ord.remark}</div>}
      </div>
    </div>
  );

  const renderTask = (t) => (
    <TaskCard
      key={t.id}
      t={t}
      infoIcon={infoIcon(t.serviceName, { exhRegistrationID: t.exhRegistrationID, company: t.exhibitor })}
      badge={badge}
      statusBadgeStyle={statusBadgeStyle}
    />
  );

  const renderPossession = (ps) => (
    <div key={ps.id} className="border rounded p-2 mb-1 bg-white">
      <div className="d-flex justify-content-between align-items-start">
        <span className="fw-semibold text-dark" style={{ fontSize: "11.5px" }}>Stall {ps.stall || "--"}</span>
        {badge(ps.status)}
      </div>
      <div className="text-muted" style={SMALL_STYLE}>
        {ps.checklist && <div><strong>Pending checklist:</strong> {ps.checklist}</div>}
        {formatDate(ps.scheduledDate) && <div><strong>Scheduled:</strong> {formatDate(ps.scheduledDate)}</div>}
        {ps.issuedBy && <div><strong>NDC issued by:</strong> {ps.issuedBy}</div>}
        {ps.takenBy && <div><strong>Taken by:</strong> {ps.takenBy}{ps.takenAt ? ` (${ps.takenAt})` : ""}</div>}
        {ps.remark && <div><strong>Remark:</strong> {ps.remark}</div>}
      </div>
    </div>
  );

  const subSection = (title, icon, items, render, emptyText) => (
    <div className="mt-2">
      <div className="fw-semibold text-secondary mb-1" style={SUBHEAD_STYLE}>
        <i className={`${icon} me-1`}></i>{title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="text-muted fst-italic" style={SMALL_STYLE}>{emptyText}</div>
      ) : (
        items.map(render)
      )}
    </div>
  );

  return (
    <div
      className="position-absolute shadow-lg bg-white d-flex flex-column border-end"
      style={{ top: 0, left: 0, width: "430px", height: "100%", zIndex: 20 }}
      onWheel={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
    >
      <div
        className="d-flex justify-content-between align-items-center p-2 border-bottom sticky-top"
        style={{ backgroundColor: "#f3f4f6" }}
      >
        <div className="d-flex align-items-center gap-2">
          <h6 className="mb-0 fw-semibold text-dark" style={{ fontSize: "14px" }}>
            {area.kind === "zone" || isVenueZone ? area.zoneName : area.hallName}
          </h6>
          <div
            className="d-flex align-items-center gap-1 rounded-pill px-2 py-1"
            style={{ backgroundColor: "#e2e8f0", fontSize: "11px", fontWeight: "500" }}
          >
            <i className={isVenueZone ? "ri-map-2-line" : "ri-building-4-line"}></i>
            <span>{isVenueZone ? `Venue Zone · ${detail.halls.length} halls` : area.kind === "zone" ? area.hallName : otherArea ? `Other Area · ${otherArea}` : "Hall"}</span>
          </div>
        </div>
        <button type="button" style={{ fontSize: "10px" }} className="btn-close" aria-label="Close" onClick={onClose}></button>
      </div>

      <div className="p-2 flex-grow-1 overflow-auto pb-3 pt-1">
        <div className="d-flex align-items-center gap-2 mb-2 p-1 bg-light rounded border">
          <span style={{ fontSize: "11px", fontWeight: "600", letterSpacing: "0.5px", textTransform: "uppercase" }}>
            Status:
          </span>
          <div
            className="flex-shrink-0"
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: getStatusColor(detail.status),
              border: "1px solid rgba(0, 0, 0, 0.15)",
            }}
          />
          <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{detail.status || "--"}</span>
          <span className="ms-auto text-muted" style={SMALL_STYLE}>
            {otherArea
              ? `${detail.servicePartners.length} service partners · ${detail.routines.length} routines`
              : <>
                {detail.exhibitors.length} exhibitors
                {flags.orders ? ` · ${detail.totals.orders} orders` : ""}
                {flags.servicePartner ? ` · ${detail.servicePartners.length} service partners` : ""}
              </>}
          </span>
        </div>

        {detail.counts.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mb-2">
            {detail.counts.map(({ status, count }) => (
              <span key={status} className="badge" style={{ ...statusBadgeStyle(status), fontSize: "10.5px" }}>
                {status}: {count}
              </span>
            ))}
          </div>
        )}

        <input
          type="search"
          // Not .form-control-sm: public/css/custom.css pins that class to
          // width 130px !important app-wide.
          className="form-control mb-2"
          style={{ width: "100%", fontSize: "12px", padding: "4px 8px", height: "30px" }}
          placeholder={otherArea ? "Search service, service partner, routine..." : "Search exhibitor, stall, service, service partner..."}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPageBy({});
          }}
        />

        {isTaskLoading && (
          <div className="small text-muted p-2">
            <span className="spinner-border spinner-border-sm me-2" role="status"></span>Loading...
          </div>
        )}

        {isVenueZone && (
          <div className="mb-2">
            <div className="fw-semibold text-dark mb-1" style={{ fontSize: "12.5px" }}>
              <i className="ri-building-4-line me-1"></i>Halls ({detail.halls.length})
            </div>
            <div className="d-flex flex-wrap gap-1">
              {detail.halls.map((h) => (
                <span
                  key={h.name}
                  className="d-inline-flex align-items-center border rounded px-2 py-1 bg-white"
                  style={{ fontSize: "11px" }}
                  title={`${h.name} - ${h.status || "No data"}`}
                >
                  <span
                    className="me-1"
                    style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: getStatusColor(h.status) }}
                  />
                  <strong className="me-1">{h.name}</strong>
                  <span className="text-muted">{h.status || "No data"}{h.stallCount ? ` · ${h.stallCount} stalls` : ""}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {otherArea && (
          <div className="small p-2 mb-2 rounded border" style={{ backgroundColor: "#fff7ed", borderColor: "#fdba74", color: "#9a3412", fontSize: "11.5px" }}>
            <i className="ri-information-line me-1"></i>
            An other area has no booths: only service partner tasks and daily routines are tracked here.
          </div>
        )}

        {/* 1. Exhibitors - each with its details and its orders */}
        {!otherArea && sectionHeader("ri-user-star-line", "Exhibitors", exhibitors.length, exhibitorIds)}

        {otherArea ? null : exhibitors.length === 0 ? (
          <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
            {q ? "Nothing matches the search." : `No exhibitor records in this ${areaLabel} for the selected data.`}
          </div>
        ) : (
          exhibitorPage.items.map((ex) => (
            <div key={ex.id} className="card shadow-sm border mb-2" style={{ borderRadius: "6px" }}>
              <div role="button" className="p-2" onClick={() => toggle(`ex:${ex.id}`)}>
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <span className="fw-semibold text-dark" style={{ fontSize: "12.5px" }}>
                    <i className={`${isOpen(`ex:${ex.id}`) ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} me-1`}></i>
                    {ex.company || "Unknown exhibitor"}
                  </span>
                  {badge(ex.status)}
                </div>
                <div className="text-muted ms-3" style={SMALL_STYLE}>
                  Stall {ex.stalls.join(", ") || "--"}
                  {flags.orders ? ` · ${ex.orders.length} orders` : ""}
                  {flags.possession ? ` · ${ex.possession.length} possession` : ""}
                </div>
              </div>

              {isOpen(`ex:${ex.id}`) && (
                <div className="px-2 pb-2 pt-1 border-top bg-light">
                  <div className="text-muted" style={SMALL_STYLE}>
                    {ex.regNo && <div><strong>Reg No:</strong> {ex.regNo}</div>}
                    {ex.contactName && <div><strong>Contact:</strong> {ex.contactName}</div>}
                    {(ex.stallType || ex.stallArea != null) && (
                      <div>
                        <strong>Stall type:</strong> {ex.stallType || "Stall"}
                        {ex.stallArea != null ? ` · ${ex.stallArea} sqm` : ""}
                      </div>
                    )}
                    {ex.mobile && <div><i className="ri-phone-line me-1"></i>{ex.mobile}</div>}
                    {ex.email && <div className="text-break"><i className="ri-mail-line me-1"></i>{ex.email}</div>}
                    {ex.country && <div><i className="ri-global-line me-1"></i>{ex.country}</div>}
                  </div>

                  {flags.orders &&
                    subSection("Exhibitor Orders & Services", "ri-shopping-cart-line", ex.orders, (o) => renderOrder(o, ex), "No orders.")}
                  {flags.possession &&
                    subSection("Stall Possession", "ri-key-2-line", ex.possession, renderPossession, "No possession record.")}
                </div>
              )}
            </div>
          ))
        )}
        <Pager paged={exhibitorPage} onPage={setPage("exhibitors")} />

        {/* 2. Service partners - each with its details and its tasks' deliverables */}
        {flags.servicePartner && (
          <>
            {sectionHeader("ri-truck-line", "Service Partners", partners.length, partnerIds)}
            {partners.length === 0 ? (
              <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                {q ? "Nothing matches the search." : `No service partner tasks in this ${areaLabel}.`}
              </div>
            ) : (
              partnerPage.items.map((sp) => (
                <div key={sp.id} className="card shadow-sm border mb-2" style={{ borderRadius: "6px" }}>
                  <div role="button" className="p-2" onClick={() => toggle(`sp:${sp.id}`)}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <span className="fw-semibold text-dark" style={{ fontSize: "12.5px" }}>
                        <i className={`${isOpen(`sp:${sp.id}`) ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} me-1`}></i>
                        {sp.company || "Unknown service partner"}
                      </span>
                      {badge(sp.status)}
                    </div>
                    <div className="text-muted ms-3" style={SMALL_STYLE}>
                      {sp.services.join(", ") || "--"} · {sp.tasks.length} tasks
                    </div>
                  </div>

                  {isOpen(`sp:${sp.id}`) && (
                    <div className="px-2 pb-2 pt-1 border-top bg-light">
                      <div className="text-muted" style={SMALL_STYLE}>
                        {sp.person && <div><i className="ri-user-3-line me-1"></i>{sp.person}</div>}
                        {sp.mobile && <div><i className="ri-phone-line me-1"></i>{sp.mobile}</div>}
                        {sp.email && <div className="text-break"><i className="ri-mail-line me-1"></i>{sp.email}</div>}
                      </div>
                      {subSection("Tasks & Deliverables", "ri-list-check-2", sp.tasks, renderTask, "No tasks.")}
                    </div>
                  )}
                </div>
              ))
            )}
            <Pager paged={partnerPage} onPage={setPage("partners")} />
          </>
        )}

        {/* 3. Daily routines - recorded per hall, not per exhibitor */}
        {flags.dailyRoutine && (
          <div className="mt-3">
            <div
              role="button"
              className="d-flex justify-content-between align-items-center mb-1"
              onClick={() => setRoutinesOpen((o) => !o)}
            >
              <span className="fw-semibold text-dark" style={{ fontSize: "12.5px" }}>
                <i className={`${routinesOpen ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} me-1`}></i>
                <i className="ri-calendar-check-line me-1 text-info"></i>Daily Routines · {routineScope}
              </span>
              <span className="badge rounded-pill bg-info" style={{ fontSize: "10px" }}>{routines.length}</span>
            </div>
            {routinesOpen &&
              (routines.length === 0 ? (
                <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                  No daily routines for {routineScope}.
                </div>
              ) : (
                routinePage.items.map((dr) => (
                  <div key={`${dr.id}|${dr.hall || ""}`} className="card shadow-none border mb-1 p-2 bg-light" style={{ borderRadius: "4px", ...SMALL_STYLE }}>
                    <div className="d-flex justify-content-between">
                      <strong>
                        {dr.title}
                        {onOpenDailyRoutine && dr.row && (
                          <span
                            role="button"
                            className="ms-1 text-primary fw-normal"
                            style={{ cursor: "pointer", fontSize: "13px", lineHeight: 1 }}
                            title={`Update ${dr.title || "this routine"}`}
                            onClick={(e) => { e.stopPropagation(); onOpenDailyRoutine(dr.row); }}
                          >
                            <i className="ri-information-line"></i>
                          </span>
                        )}
                      </strong>
                      {badge(dr.status)}
                    </div>
                    <div className="text-muted mt-1">
                      {isVenueZone && dr.hall && <div>Hall: {dr.hall}</div>}
                      {dr.times && <div>Time: {dr.times}</div>}
                      {dr.responsible && <div>Responsible: {dr.responsible}</div>}
                      <div>Completed: {dr.done}/{dr.total}</div>
                    </div>
                  </div>
                ))
              ))}
            {routinesOpen && <Pager paged={routinePage} onPage={setPage("routines")} />}
          </div>
        )}

        {/* The drawn stalls, each leading to its own popup */}
        {!otherArea && (
        <div className="mt-3">
          <div
            role="button"
            className="d-flex justify-content-between align-items-center mb-1"
            onClick={() => setStallsOpen((o) => !o)}
          >
            <span className="fw-semibold text-dark" style={{ fontSize: "12.5px" }}>
              <i className={`${stallsOpen || q ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} me-1`}></i>
              <i className="ri-store-2-line me-1 text-secondary"></i>Stalls on the plan
            </span>
            <span className="badge rounded-pill bg-secondary" style={{ fontSize: "10px" }}>{stalls.length}</span>
          </div>
          {(stallsOpen || q) &&
            (stalls.length === 0 ? (
              <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                No stalls of this {areaLabel} are on the drawing.
              </div>
            ) : (
              stallGroups.map(([hall, group]) => (
                <div key={hall || "all"} className="mb-1">
                  {hall && (
                    <div className="text-muted fw-semibold" style={{ fontSize: "10.5px" }}>
                      {hall} ({group.length})
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-1">
                    {group.map((s) => (
                      <span
                        key={s.name}
                        role="button"
                        className="d-inline-flex align-items-center border rounded px-2 py-0 bg-white"
                        style={{ fontSize: "11px", cursor: "pointer" }}
                        title={`${s.name} - ${s.label}. Click for the stall's own details.`}
                        onClick={() => onSelectBooth(s.booth)}
                      >
                        <span
                          className="me-1"
                          style={{
                            display: "inline-block",
                            width: "7px",
                            height: "7px",
                            borderRadius: "50%",
                            backgroundColor: getStatusColor(s.status || s.label),
                          }}
                        />
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            ))}
          {(stallsOpen || q) && <Pager paged={stallPage} onPage={setPage("stalls")} />}
        </div>
        )}
      </div>
    </div>
  );
};

export default AreaDetailPanel;
